import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Bell, Mail, MessageSquare, Save, ArrowLeft, History, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: true,
    notifications: {
      patient_registration: { email: true, sms: true },
      appointment_reminder: { email: true, sms: true },
      usage_alerts: { email: true, sms: false },
      subscription_alerts: { email: true, sms: false },
      payment_confirmation: { email: true, sms: false }
    }
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences(response.data);
    } catch (error) {
      logger.error('Failed to load preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/preferences', preferences);
      toast.success('Notification preferences saved successfully');
    } catch (error) {
      logger.error('Failed to save preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleMasterSwitch = (type) => {
    setPreferences(prev => ({
      ...prev,
      [`${type}_enabled`]: !prev[`${type}_enabled`]
    }));
  };

  const toggleNotification = (notificationType, channel) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [notificationType]: {
          ...prev.notifications[notificationType],
          [channel]: !prev.notifications[notificationType][channel]
        }
      }
    }));
  };

  const notificationTypes = [
    {
      key: 'patient_registration',
      label: 'Patient Registration',
      description: 'Notifications when a new patient is registered',
      icon: '👤'
    },
    {
      key: 'appointment_reminder',
      label: 'Appointment Reminders',
      description: 'Reminders for upcoming appointments',
      icon: '📅'
    },
    {
      key: 'usage_alerts',
      label: 'Usage Alerts',
      description: 'Alerts when approaching usage limits',
      icon: '⚠️'
    },
    {
      key: 'subscription_alerts',
      label: 'Subscription Alerts',
      description: 'Alerts for subscription expiry and renewals',
      icon: '💳'
    },
    {
      key: 'payment_confirmation',
      label: 'Payment Confirmations',
      description: 'Confirmations for successful payments',
      icon: '✅'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings/profile')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Notification Preferences</h1>
            <p className="text-slate-500">Manage your email and SMS notification settings</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/settings/notifications/analytics')}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/settings/notifications/history')}
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>
      </header>

      <div className="page-content max-w-4xl">
        <div className="space-y-6">
          {/* Master Switches */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Bell className="w-5 h-5 text-red-500" />
                Master Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive notifications via email</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterSwitch('email')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.email_enabled ? 'bg-red-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.email_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-slate-900">SMS Notifications</p>
                    <p className="text-sm text-slate-500">Receive notifications via SMS</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterSwitch('sms')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences.sms_enabled ? 'bg-red-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences.sms_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Individual Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Notification Types</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Choose which notifications you want to receive</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {notificationTypes.map((notif) => (
                <div key={notif.key} className="border-b border-slate-200 last:border-0 pb-6 last:pb-0">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">{notif.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{notif.label}</p>
                      <p className="text-sm text-slate-500">{notif.description}</p>
                    </div>
                  </div>
                  
                  <div className="ml-11 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">Email</Label>
                      <button
                        onClick={() => toggleNotification(notif.key, 'email')}
                        disabled={!preferences.email_enabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.notifications[notif.key]?.email && preferences.email_enabled
                            ? 'bg-blue-500'
                            : 'bg-slate-300'
                        } ${!preferences.email_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.notifications[notif.key]?.email && preferences.email_enabled
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">SMS</Label>
                      <button
                        onClick={() => toggleNotification(notif.key, 'sms')}
                        disabled={!preferences.sms_enabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.notifications[notif.key]?.sms && preferences.sms_enabled
                            ? 'bg-green-500'
                            : 'bg-slate-300'
                        } ${!preferences.sms_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.notifications[notif.key]?.sms && preferences.sms_enabled
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/settings/profile')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-red-500 hover:bg-red-600"
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
